import React from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, getApiErrorMessage } from "@/services/api/client";

type FeedbackType = "bug" | "feature" | "general";

const TYPE_OPTIONS: Array<{ value: FeedbackType; label: string }> = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
  { value: "general", label: "General" },
];

function parseFeedbackType(value: string): FeedbackType | null {
  return TYPE_OPTIONS.find((option) => option.value === value)?.value ?? null;
}

function getRouteMetadata(pathname: string): Record<string, unknown> {
  const segments = pathname.split("/").filter(Boolean);
  const metadata: Record<string, unknown> = { pathname };

  const projectIndex = segments.findIndex((segment) =>
    ["editor", "viewer", "view"].includes(segment),
  );
  if (projectIndex >= 0 && segments[projectIndex + 1]) {
    metadata.projectId = segments[projectIndex + 1];
  }

  const pageIndex = segments.findIndex((segment) => segment === "page");
  if (pageIndex >= 0 && segments[pageIndex + 1]) {
    metadata.pageId = segments[pageIndex + 1];
  }

  return metadata;
}

interface FeedbackFormProps {
  onSuccess?: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSuccess }) => {
  const location = useLocation();
  const [type, setType] = React.useState<FeedbackType>("general");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  const successTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setError("Add a short note before sending.");
      return;
    }

    if (trimmedMessage.length > 2000) {
      setError("Feedback must be 2000 characters or fewer.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await api.post("/feedback", {
        type,
        subject: subject.trim() || undefined,
        message: trimmedMessage,
        url: window.location.href,
        metadata: getRouteMetadata(location.pathname),
      });
      setSubmitted(true);
      successTimerRef.current = window.setTimeout(() => {
        setType("general");
        setSubject("");
        setMessage("");
        setError(null);
        setSubmitted(false);
        onSuccess?.();
      }, 2000);
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, "Feedback could not be sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full p-4 gap-4">
      {submitted ? (
        <div className="flex-1 flex items-center justify-center text-sm font-medium text-foreground">
          Thanks - we'll review it soon
        </div>
      ) : (
        <>
          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor="feedback-type">
              Type
            </label>
            <select
              id="feedback-type"
              value={type}
              onChange={(event) => {
                const nextType = parseFeedbackType(event.target.value);
                if (nextType) {
                  setType(nextType);
                }
              }}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium" htmlFor="feedback-subject">
              Subject
            </label>
            <Input
              id="feedback-subject"
              maxLength={200}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Brief summary"
            />
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <label className="text-sm font-medium" htmlFor="feedback-message">
                Message
              </label>
              <span className="text-xs text-muted-foreground">{message.length}/2000</span>
            </div>
            <Textarea
              id="feedback-message"
              required
              maxLength={2000}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="What should we know?"
              className="flex-1 min-h-[120px] resize-none"
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || message.trim().length === 0}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </Button>
        </>
      )}
    </form>
  );
};
